import { create } from 'zustand';
import { mmkv } from '@/storage/mmkv';
import type { CategoryId } from '@/guidance/capabilities';
import type { AppKind } from '@/types/appKind';
import type { SelectedPlan, Plan } from '@/llm/planTypes';
import type { AppPlan } from '@/webapp/plan/planTypes';

export type Intent = 'build' | 'browse';
export interface ClarifyMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface OnboardingState {
  intent: Intent | null;
  category: CategoryId | null;
  features: string[];
  description: string;
  name: string;
  clarifyMessages: ClarifyMessage[];
  pendingIntent: Intent | null;
  flowPatterns: string[];
  flowNote: string;
  vibe: string | null;
  kind: AppKind;
  /** Plan-first: reference images (data-URIs) + the chosen layout/style. In-memory
   *  per-build (not persisted — base64 is large); cleared on reset(). */
  vibeImages: string[];
  /** The guided front door's plan — what the user reviewed and edited, and the only
   *  wizard state the web-app generator is actually given. */
  appPlan: AppPlan | null;
  planResult: Plan | null;            // the planning result (options to choose from)
  selectedPlan: SelectedPlan | null;  // the chosen option + tweak
  setIntent: (i: Intent) => void;
  setCategory: (c: CategoryId) => void;
  toggleFeature: (id: string) => void;
  setDescription: (d: string) => void;
  setName: (n: string) => void;
  addClarifyMessage: (m: ClarifyMessage) => void;
  setPendingIntent: (i: Intent | null) => void;
  setFlowPatterns: (ids: string[]) => void;
  setFlowNote: (note: string) => void;
  setVibe: (v: string | null) => void;
  setFeatures: (ids: string[]) => void;
  setKind: (k: AppKind) => void;
  setVibeImages: (imgs: string[]) => void;
  setAppPlan: (p: AppPlan | null) => void;
  setPlanResult: (p: Plan | null) => void;
  setSelectedPlan: (p: SelectedPlan | null) => void;
  seedEnabled: boolean;
  seedSource: 'knowledge' | 'web';
  seedDescription: string;
  setSeedEnabled: (v: boolean) => void;
  setSeedSource: (s: 'knowledge' | 'web') => void;
  setSeedDescription: (d: string) => void;
  aiEnabled: boolean;
  aiDescription: string;
  setAiEnabled: (v: boolean) => void;
  setAiDescription: (d: string) => void;
  /** Which GENERATION MODE the next build uses (kept named `runtime` for backward
   *  compatibility with existing tests/call sites — see
   *  .superpowers/sdd/2026-08-28-dual-mode-3-mode-switch/translate-pipeline-report.md
   *  for why it wasn't renamed). 'engine' (default) = the JSON engine builds the app
   *  directly. 'miniapp' = generate real React Native and RUN it via src/miniapp at
   *  render time (experimental). 'translate' = generate real React Native, then
   *  TRANSLATE it at build time into a plain AppDefinition — the shipped app's
   *  runtime is the engine either way; only how it got authored differs
   *  (experimental). */
  runtime: 'engine' | 'miniapp' | 'translate' | 'webapp';
  setRuntime: (runtime: 'engine' | 'miniapp' | 'translate' | 'webapp') => void;
  reset: () => void;
}

const KEY = 'onboarding_state';
const persisted = (() => {
  try {
    return JSON.parse(mmkv.getString(KEY) ?? '{}');
  } catch {
    return {};
  }
})();

const initial = {
  intent: null,
  category: null,
  features: [],
  description: '',
  name: '',
  clarifyMessages: [],
  pendingIntent: null,
  flowPatterns: [],
  flowNote: '',
  vibe: null,
  kind: 'personal' as AppKind,
  vibeImages: [] as string[],
  appPlan: null as AppPlan | null,
  planResult: null as Plan | null,
  selectedPlan: null as SelectedPlan | null,
  seedEnabled: false,
  seedSource: 'knowledge' as 'knowledge' | 'web',
  seedDescription: '',
  aiEnabled: false,
  aiDescription: '',
  // 'webapp' is the ONLY mode the UI can now reach: Saddle builds mobile-native web
  // apps. The other three are kept so existing projects and the bundled demos still
  // render, but nothing routes to them any more — see the pivot design doc.
  runtime: 'webapp' as 'engine' | 'miniapp' | 'translate' | 'webapp',
};

export const useOnboardingStore = create<OnboardingState>((set, get) => {
  const save = () => {
    const { intent, category, features, description, name, clarifyMessages, pendingIntent, flowPatterns, flowNote, vibe, kind, seedEnabled, seedSource, seedDescription, aiEnabled, aiDescription } = get();
    mmkv.setString(KEY, JSON.stringify({ intent, category, features, description, name, clarifyMessages, pendingIntent, flowPatterns, flowNote, vibe, kind, seedEnabled, seedSource, seedDescription, aiEnabled, aiDescription }));
  };

  return {
    ...initial,
    ...persisted,
    setIntent: (intent) => {
      set({ intent });
      save();
    },
    setCategory: (category) => { set({ category }); save(); },
    toggleFeature: (id) => {
      set((s) => ({ features: s.features.includes(id) ? s.features.filter((f) => f !== id) : [...s.features, id] }));
      save();
    },
    setDescription: (description) => {
      set({ description });
      save();
    },
    setName: (name) => {
      set({ name });
      save();
    },
    addClarifyMessage: (m) => {
      set((s) => ({ clarifyMessages: [...s.clarifyMessages, m] }));
      save();
    },
    setPendingIntent: (pendingIntent) => {
      set({ pendingIntent });
      save();
    },
    setFlowPatterns: (flowPatterns) => { set({ flowPatterns }); save(); },
    setFlowNote: (flowNote) => { set({ flowNote }); save(); },
    setVibe: (vibe) => { set({ vibe }); save(); },
    setFeatures: (features) => { set({ features }); save(); },
    setKind: (kind) => { set({ kind }); save(); },
    setVibeImages: (vibeImages) => set({ vibeImages }),       // not persisted (large)
    setAppPlan: (appPlan) => set({ appPlan }),
    setPlanResult: (planResult) => set({ planResult }),
    setSelectedPlan: (selectedPlan) => set({ selectedPlan }),
    setSeedEnabled: (seedEnabled) => { set({ seedEnabled }); save(); },
    setSeedSource: (seedSource) => { set({ seedSource }); save(); },
    setSeedDescription: (seedDescription) => { set({ seedDescription }); save(); },
    setAiEnabled: (aiEnabled) => { set({ aiEnabled }); save(); },
    setAiDescription: (aiDescription) => { set({ aiDescription }); save(); },
    setRuntime: (runtime) => set({ runtime }),
    reset: () => {
      mmkv.delete(KEY);
      set({ ...initial });
    },
  };
});
